import React, { useEffect, useMemo, useRef, useState } from "react";
import { DailogBox } from "../../../components/Elements/DailogBox/DailogBox";
import CardsBorder from "../../../components/Cards/CardsBorder";
import SelectList from "../../../components/common/SelectList";
import { ReactComponent as CalenderIcon } from "../../../assets/svgs/Calendar.svg";
import ThemedCalendar from "../../../components/Elements/CustomDatePicker/ThemedCalender";
import { Button } from "antd";
import { getApi, postApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import dayjs from "dayjs";
// import toast from "react-hot-toast";

const EditProjectSetUp = ({
  open,
  onCancel,
  projectId,
  projectData,
  setProjectSetup,
}) => {
  const [selectedForms, setSelectedForms] = useState([]);
  const [selectLicenseData, setSelectLicenseData] = useState({});
  const [selectedDynamicFormAccess, setSelectedDynamicFormAccess] =
    useState("");
  const [canCustomerAssignSection, setCanCustomerAssignSection] = useState("");
  const [validTillDate, setValidTillDate] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [page, setPage] = useState(1);
  const [allForms, setAllForms] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 3;

  const calendarRef = useRef(null);

  const selectLicenseList = useMemo(
    () => [
      { label: "SUB-ADMIN", value: "sub-admin" },
      { label: "Location", value: "location" },
      { label: "EMPLOYEE", value: "employee" },
      { label: "VENDOR", value: "vendor" },
      { label: "MANAGEMENT", value: "management" },
    ],
    []
  );

  const yesNOList = useMemo(
    () => [
      { label: "YES", value: "yes" },
      { label: "NO", value: "no" },
    ],
    []
  );

  const getStandardForms = async (pageNumber = 1, append = false) => {
    try {
      const response = await getApi({
        apiUrl: API_URLS.GET_STANDARDFORMS(pageSize, pageNumber),
      });

      if (response.success) {
        const filteredData = response.data.filter(
          (item) => item.status === "Approved"
        );

        const formattedData = filteredData.map((item, index) => ({
          formId: item._id,
          serial: (pageNumber - 1) * pageSize + index + 1,
          label: item.title,
          value: item._id,
          date: dayjs(item.updatedAt).format("YYYY/MM/DD"),
        }));

        const updatedForms = append
          ? [...allForms, ...formattedData]
          : formattedData;

        setAllForms(updatedForms);

        if (response.total <= response.skip + pageSize) {
          setHasMore(false);
        }
      }
    } catch (error) {
      console.error("Error in fetching standard form data", error);
    }
  };

  const handleDateChange = (date) => {
    setValidTillDate(date);
    setShowCalendar(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);

  const handleSelectedFormChange = (value) => {
    setSelectedForms((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  const handleLicenseDataChange = (value, key) => {
    setSelectLicenseData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSeeMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    getStandardForms(nextPage, true);
  };

  const createProjectSetUp = async (projectId, formData) => {
    try {
      const response = await postApi({
        apiUrl: API_URLS.CREATE_PROJECT_SETUP(projectId),
        data: formData,
      });

      if (response.success) {
        // toast.success(response.message);
        setProjectSetup(true);
        onCancel();
      }
    } catch {
      // toast.error("Error in setting up the project");
    }
  };

  const handleSubmit = () => {
    const checkedForms = allForms
      .filter((form) => selectedForms.includes(form.value))
      .map((form) => ({
        id: form.formId,
        date: dayjs(form.date).format("YYYY-MM-DD"),
      }));

    const uncheckedForms = allForms
      .filter((form) => !selectedForms.includes(form.value))
      .map((form) => ({ id: form.formId }));

    const license = {
      ...(selectLicenseData["sub-admin"] && {
        location_admin: Number(selectLicenseData["sub-admin"]),
      }),
      ...(selectLicenseData["location"] && {
        location_user: Number(selectLicenseData["location"]),
      }),
      ...(selectLicenseData["employee"] && {
        employee: Number(selectLicenseData["employee"]),
      }),
      ...(selectLicenseData["vendor"] && {
        vendor: Number(selectLicenseData["vendor"]),
      }),
    };

    const formData = {
      standard_form: {
        checked: checkedForms,
        unchecked: uncheckedForms,
      },
      license,
      dynamic_form_access: selectedDynamicFormAccess === "yes",
      section_access: canCustomerAssignSection === "yes",
      valid_till_end_date: validTillDate
        ? dayjs(validTillDate).format("YYYY-MM-DD")
        : null,
    };

    console.log("Form Data Submitted:", formData);
    createProjectSetUp(projectId, formData);
  };

  useEffect(() => {
    getStandardForms(1, false);
  }, []);

  // ✅ Prefill logic from projectData
  useEffect(() => {
    if (projectData) {
      // 1. Auto select standard forms
      if (projectData.standard_form?.length > 0) {
        const formIds = projectData.standard_form.map((form) => form.id);
        setSelectedForms(formIds);
      }

      // 2. Set valid till end date
      if (projectData.valid_till_end_date) {
        setValidTillDate(
          dayjs(projectData.valid_till_end_date).format("YYYY-MM-DD")
        );
      }

      // 3. Set yes/no radio values
      if (typeof projectData.dynamic_form_access === "boolean") {
        setSelectedDynamicFormAccess(
          projectData.dynamic_form_access ? "yes" : "no"
        );
      }

      if (typeof projectData.section_access === "boolean") {
        setCanCustomerAssignSection(projectData.section_access ? "yes" : "no");
      }

      // ✅ 4. Prefill license details
      const licensePrefill = {
        ...(projectData.location_admin !== undefined && {
          "sub-admin": String(projectData.location_admin),
        }),
        ...(projectData.location_user !== undefined && {
          location: String(projectData.location_user),
        }),
        ...(projectData.employee !== undefined && {
          employee: String(projectData.employee),
        }),
        ...(projectData.vendor !== undefined && {
          vendor: String(projectData.vendor),
        }),
        ...(projectData.management !== undefined && {
          management: String(projectData.management),
        }),
      };

      setSelectLicenseData(licensePrefill);
    }
  }, [projectData]);

  return (
    <DailogBox open={open} onCancel={onCancel} width={800}>
      <div className="p-8">
        <CardsBorder>
          <div className="py-6">
            <SelectList
              list={allForms}
              title="Select Forms"
              selecteditems={selectedForms}
              onChange={handleSelectedFormChange}
              selectionType="checkbox"
            />
          </div>
          <div className="flex justify-center py-4">
            {hasMore > 0 && (
              <div
                className="text-center w-44 border border-2-solid border-blue-800 rounded-full px-6 py-2 text-blue-800 cursor-pointer"
                onClick={handleSeeMore}
              >
                See More
              </div>
            )}
          </div>
        </CardsBorder>

        <div className="mt-6">
          <CardsBorder>
            <div className="py-6">
              <SelectList
                list={selectLicenseList}
                title="License"
                selecteditems={selectLicenseData}
                onChange={handleLicenseDataChange}
                selectionType="number"
              />
            </div>
          </CardsBorder>
        </div>

        <div className="mt-6">
          <CardsBorder>
            <div className="py-6">
              <SelectList
                list={yesNOList}
                title="Use Dynamic Form in this Project"
                selecteditems={selectedDynamicFormAccess}
                onChange={setSelectedDynamicFormAccess}
                selectionType="radio"
              />
            </div>
          </CardsBorder>
        </div>

        <div className="mt-6">
          <CardsBorder>
            <div className="py-6">
              <SelectList
                list={yesNOList}
                title="Can Customer Assign Section of any form"
                selecteditems={canCustomerAssignSection}
                onChange={setCanCustomerAssignSection}
                selectionType="radio"
              />
            </div>
          </CardsBorder>
        </div>

        <div className="mt-6 relative">
          <CardsBorder>
            <div className="py-6">
              <div className="px-10">
                <h4 className="text-2xl font-semibold">Valid Till End Date</h4>
                <hr className="my-4" />
              </div>
              <div className="space-y-6">
                <div
                  className="flex justify-between items-center border-b pb-4 cursor-pointer"
                  onClick={() => setShowCalendar(true)}
                >
                  <span className="text-sm mx-14 font-light">
                    {validTillDate || "DD/MM/YYYY"}
                  </span>
                  <CalenderIcon className="mx-10" />
                </div>
              </div>
            </div>
          </CardsBorder>

          {showCalendar && (
            <div
              className="absolute top-16 left-1/2 transform -translate-x-1/2 z-50"
              ref={calendarRef}
            >
              <ThemedCalendar
                value={validTillDate}
                onChange={handleDateChange}
              />
            </div>
          )}
        </div>

        <div className="text-center my-8">
          <Button
            type="primary"
            style={{ background: "#06513D", borderRadius: "10px" }}
            className="text-sm py-5 px-6"
            onClick={handleSubmit}
          >
            Submit For Approval
          </Button>
        </div>
      </div>
    </DailogBox>
  );
};

export default EditProjectSetUp;
